import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import ClientDropdown from "components/Dropdowns/ClientDropdown";
import InputField from "components/Input/InputField";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { differenceInDays } from "date-fns";
import { formatDisplayDate, parseToDate } from "utils/dateFormat";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getThemedTabsSx } from "theme/tabsTheme";
import EntryDocumentsPanel from "components/ModuleDocuments/EntryDocumentsPanel";
import SendNotificationModal from "../Accounts/SendNotificationModal";
import ModulePaymentsTab from "components/ModulePayments/ModulePaymentsTab";
import ModuleTasksTab from "components/Tasks/ModuleTasksTab";
import SkillAssessmentStatusHistoryTab from "./AddSkillAssessment/SkillAssessmentStatusHistoryTab";
import { useSkillAssessmentStatusHistory } from "./skillAssessmentStatusHistoryApi";
import { usePreselectClientFromLocation } from "../shared/moduleClients/usePreselectClientFromLocation";
import ModuleLockedTab from "../shared/ModuleLockedTab";
import { blankIfPlaceholder } from "utils/textNormalize";
import { computeFinalAmount, computeTotal, defaultGstSetting } from "utils/gstCalc";
import GstChargeField from "components/GstChargeField";
import ViewClientProfile from "components/Clients/ViewClientProfile";
import { promptReferralShare } from "../Clients/ReferralSharePrompt";

const TAB_DETAILS = 0;
const TAB_DOCUMENTS = 1;
const TAB_STATUS = 2;
const TAB_PAYMENTS = 3;
const TAB_TASKS = 4;

const LEGACY_DOCUMENT_FIELDS = [
  { key: "resume", name: "Updated Resume" },
  { key: "passport", name: "Full Passport" },
  { key: "academic", name: "Academic Document" },
  { key: "py", name: "PY Certificate" },
];

const normalizeSkillDocuments = (item = {}) => {
  if (Array.isArray(item.documents) && item.documents.length > 0) {
    return item.documents;
  }

  const migrated = [];
  LEGACY_DOCUMENT_FIELDS.forEach(({ key, name }) => {
    const value = item[key];
    if (!value) return;
    const urls = Array.isArray(value) ? value.filter(Boolean) : [value];
    urls.forEach((url, index) => {
      migrated.push({
        id: crypto.randomUUID(),
        name: urls.length > 1 ? `${name} ${index + 1}` : name,
        url,
        addedAt: item?.updatedAt || item?.createdAt || new Date().toISOString(),
        addedBy: null,
      });
    });
  });
  return migrated;
};

const parseBoolFlag = (value) => value === true || value === "true";

const INITIAL_STATE = {
  clientId: null,
  name: null,
  address: null,
  dob: null,
  gender: null,
  email: null,
  number: null,
  passportNumber: null,
  passportExpiry: "",
  dateOfIssue: "",
  visaStatus: "",
  visaExpiry: "",
  caseOfficer: "",
  reference: "",
  courseProvider: "",
  courseCompleted: "",
  occupation: "",
  cost: "",
  serviceCharge: "",
  extraCharges: "",
  kotharCost: "",
  discount: "",
  costGst: defaultGstSetting(false),
  serviceChargeGst: defaultGstSetting(true),
  extraChargesGst: defaultGstSetting(false),
  kotharCostGst: defaultGstSetting(false),
  clientStatus: "",
  documents: [],
  importantNote: "",
  status: false,
  completeStatus: false,
};

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const formatClientDate = (value) => {
  if (!value) return null;
  return formatDisplayDate(value, null);
};

const getExpiryMeta = (value) => {
  const display = formatClientDate(value);
  if (!display) {
    return { display: "Not provided", chip: null };
  }

  const parsed = parseToDate(value);
  const days = parsed ? differenceInDays(parsed, new Date()) : null;

  if (days == null) {
    return { display, chip: null };
  }
  if (days < 0) {
    return { display, chip: { label: "Expired", color: "error" } };
  }
  if (days <= 90) {
    return {
      display,
      chip: { label: `Expires in ${days} day${days === 1 ? "" : "s"}`, color: "warning" },
    };
  }
  return { display, chip: null };
};

const CompactDetail = ({ label, value, chip }) => (
  <Box className="min-w-0">
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: 10, letterSpacing: 0.4, fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.25 }}>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ fontSize: 13 }}
        className="text-app-text break-words"
      >
        {value || "—"}
      </Typography>
      {chip && (
        <Chip
          size="small"
          label={chip.label}
          color={chip.color}
          variant="outlined"
          sx={{ height: 20, fontSize: 11 }}
        />
      )}
    </Stack>
  </Box>
);

const ClientSummaryCard = ({ data, clientList, onViewClient }) => {
  const clientId = data?.clientId ?? data?.id;
  if (!clientId) return null;

  const storedClient = clientList?.find((client) => client.id === clientId) || {};
  const profile = {
    ...storedClient,
    ...data,
    clientId,
    visaStatus:
      blankIfPlaceholder(data?.visaStatus) || blankIfPlaceholder(storedClient?.visaStatus),
    caseOfficer: data?.caseOfficer || storedClient?.caseOfficer,
  };

  const visaExpiry = getExpiryMeta(profile.visaExpiry);
  const passportExpiry = getExpiryMeta(profile.passportExpiry);

  return (
    <Paper variant="outlined" className="border-app-border">
      <Box className="px-3 py-2">
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Stack direction="row" spacing={1.25} alignItems="center" className="min-w-0">
            <Avatar
              sx={{ bgcolor: "#ea580c", width: 32, height: 32, fontSize: 13, fontWeight: 700 }}
            >
              {getInitials(profile.name)}
            </Avatar>
            <Box className="min-w-0">
              <Typography variant="body2" fontWeight={700} className="truncate">
                {profile.name || "—"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                className="block truncate"
                sx={{ fontSize: 11 }}
              >
                {[profile.email, profile.number].filter(Boolean).join(" · ") || "—"}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="text"
            size="small"
            startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            onClick={onViewClient}
            sx={{ flexShrink: 0, minWidth: "auto", px: 0.75, py: 0.25, fontSize: 12 }}
          >
            View
          </Button>
        </Stack>

        <Box className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1.5 mt-2 pt-2 border-t border-app-border">
          <CompactDetail label="Current visa" value={profile.visaStatus || "Not provided"} />
          <CompactDetail label="Visa expiry" value={visaExpiry.display} chip={visaExpiry.chip} />
          <CompactDetail label="Passport" value={profile.passportNumber || "Not provided"} />
          <CompactDetail
            label="Passport expiry"
            value={passportExpiry.display}
            chip={passportExpiry.chip}
          />
          <CompactDetail label="Case officer" value={profile.caseOfficer || "Not assigned"} />
          <CompactDetail
            label="Reference"
            value={profile.reference || profile.referral || "Not provided"}
          />
        </Box>
      </Box>
    </Paper>
  );
};

/** Shown in the Status history / Payments tabs before the assessment has been saved — both are keyed off a real skillAssessmentId. */
const SaveFirstNotice = () => (
  <Paper variant="outlined" sx={{ p: 3 }}>
    <Typography variant="body2" color="text.secondary" className="text-center">
      Save the skill assessment first to start tracking status updates and payments.
    </Typography>
  </Paper>
);

const TAB_QUERY_PARAM_TO_INDEX = {
  details: TAB_DETAILS,
  documents: TAB_DOCUMENTS,
  status: TAB_STATUS,
  payments: TAB_PAYMENTS,
  tasks: TAB_TASKS,
};

const AddSkillAssessment = () => {
  const [data, setData] = useState(INITIAL_STATE);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    () => TAB_QUERY_PARAM_TO_INDEX[searchParams.get("tab")] ?? TAB_DETAILS
  );
  const [notifyOpen, setNotifyOpen] = useState(false);

  const { id: routeId } = useParams();
  const [{ usersList, clientList }, { refetchClient, refetchUsers }] = useKothar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state } = useLocation();

  useEffect(() => {
    if (!clientList?.length) refetchClient();
  }, [clientList?.length, refetchClient]);

  useEffect(() => {
    if (!usersList?.length) refetchUsers();
  }, [usersList?.length, refetchUsers]);

  usePreselectClientFromLocation({ setData, editId: routeId || state?.item?.id });

  const clientId = data?.clientId ?? data?.id;
  const recordId = data?.id || routeId;

  const { data: statusHistory = [] } = useSkillAssessmentStatusHistory(recordId, Boolean(recordId));

  const hydratedFromServerRef = useRef(false);

  const { data: fetchedSkill } = useQuery(
    ["skillAssessmentDetail", routeId],
    async () => {
      const res = await axios.get(`${API_URL}/skill-assessment/${routeId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      return res?.data?.data ?? null;
    },
    { enabled: !!routeId, staleTime: 0, cacheTime: 0, refetchOnWindowFocus: false }
  );

  // Server payload first (form draft hydrates once); the tab badge shows only when > 0.
  const openSkillTaskCount = Number(fetchedSkill?.openTaskCount ?? data?.openTaskCount ?? 0);

  useEffect(() => {
    const item = state?.item;
    if (!item || hydratedFromServerRef.current) return;
    setData({
      ...INITIAL_STATE,
      ...item,
      client: item?.name,
      status: parseBoolFlag(item?.status),
      completeStatus: parseBoolFlag(item?.completeStatus),
      documents: normalizeSkillDocuments(item),
    });
  }, [state]);

  useEffect(() => {
    if (!fetchedSkill || hydratedFromServerRef.current) return;
    hydratedFromServerRef.current = true;
    setData({
      ...INITIAL_STATE,
      ...fetchedSkill,
      client: fetchedSkill?.name,
      status: parseBoolFlag(fetchedSkill?.status),
      completeStatus: parseBoolFlag(fetchedSkill?.completeStatus),
      documents: normalizeSkillDocuments(fetchedSkill),
    });
  }, [fetchedSkill]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const relatedApplicationState = useMemo(() => {
    if (!clientId) return null;
    const storedClient = clientList?.find((client) => client.id === clientId) || {};
    return { ...storedClient, ...data, clientId };
  }, [clientId, clientList, data]);

  const clientCaseOfficer = clientList?.find((client) => client.id === clientId)?.caseOfficer;

  const openVisaApplication = () => {
    if (!relatedApplicationState) {
      toast.error("Please select a client first");
      return;
    }
    navigate("/admin/visa/add", { state: { item: relatedApplicationState } });
  };

  const openInsurance = () => {
    if (!relatedApplicationState) {
      toast.error("Please select a client first");
      return;
    }
    navigate("/admin/insurance/add", { state: { item: relatedApplicationState } });
  };

  const exitAfterSaveRef = useRef(false);

  const { mutate, isLoading: saving } = useMutation(postData, {
    onSuccess(saved) {
      toast.success(recordId ? "Skill assessment updated" : "Skill assessment created");
      if (saved?.id && !recordId) {
        setData((prev) => ({ ...prev, id: saved.id }));
        promptReferralShare({
          module: "skill-assessment",
          entryId: saved.id,
          clientId: saved?.clientId ?? clientId,
        });
      }
      queryClient.invalidateQueries(["skill-clients-page"]);
      const savedClientId = saved?.clientId ?? clientId;
      if (savedClientId != null) {
        queryClient.invalidateQueries(["skill-client-records", String(savedClientId)]);
      }
      if (exitAfterSaveRef.current) {
        navigate("/admin/skill-assessment");
      }
    },
    onError() {
      toast.error(recordId ? "Error updating skill assessment" : "Error saving skill assessment");
    },
  });

  async function postData(payload) {
    if (recordId) {
      const res = await axios.put(`${API_URL}/skill-assessment/${payload?.id}`, payload);
      return res?.data?.data ?? null;
    }
    const res = await axios.post(`${API_URL}/skill-assessment`, payload);
    return res?.data?.data ?? null;
  }

  const handleSubmit = (e, exitAfter = false) => {
    e.preventDefault();
    if (!clientId) {
      toast.error("Please select a client");
      setActiveTab(TAB_DETAILS);
      return;
    }
    exitAfterSaveRef.current = exitAfter;
    mutate({
      ...data,
      clientId,
      status: data.status ? "true" : "false",
      completeStatus: data.completeStatus ? "true" : "false",
    });
  };

  const notifyContext = useMemo(
    () => ({
      clientId,
      caseOfficer: data?.caseOfficer,
      module: "skill-assessment",
    }),
    [clientId, data?.caseOfficer]
  );

  const defaultNotifyMessage = useMemo(() => {
    const clientName = data?.name || "Client";
    const parts = [
      `Skill assessment payment update for ${clientName}.`,
      data?.occupation ? `Occupation: ${data.occupation}.` : null,
    ].filter(Boolean);
    return parts.join(" ");
  }, [data?.name, data?.occupation]);

  const currentWorkflowStatus = statusHistory?.[0]?.toStatus || data?.workflowStatus;

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <SendNotificationModal
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        users={usersList || []}
        context={{ ...notifyContext, defaultMessage: defaultNotifyMessage }}
      />

      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t px-4 py-4 border-b border-app-border">
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <IconButton size="small" onClick={() => navigate(-1)} aria-label="Go back">
                  <IoArrowBack className="text-xl" />
                </IconButton>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" fontWeight={700}>
                      {recordId ? "Edit skill assessment" : "Add skill assessment"}
                    </Typography>
                    {data?.migrated && (
                      <Tooltip title="Migrated from legacy data — complete the missing details">
                        <Chip
                          label="Migrated"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontSize: 9, height: 18 }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Manage assessment details, documents, status history, and payments
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                {currentWorkflowStatus && (
                  <Chip
                    label={currentWorkflowStatus}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Stack>
            </Stack>
          </div>

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={getThemedTabsSx()}
          >
            <Tab label="Details" />
            <ModuleLockedTab
              label="Documents"
              locked={!recordId}
              lockMessage="Create the assessment first to unlock this tab"
            />
            <ModuleLockedTab
              label="Status history"
              locked={!recordId}
              lockMessage="Create the assessment first to unlock this tab"
            />
            <ModuleLockedTab
              label="Payments"
              locked={!recordId}
              lockMessage="Create the assessment first to unlock this tab"
            />
            <ModuleLockedTab
              label={openSkillTaskCount > 0 ? `Tasks (${openSkillTaskCount})` : "Tasks"}
              locked={!recordId}
              lockMessage="Create the assessment first to unlock this tab"
            />
          </Tabs>

          <form onSubmit={(e) => handleSubmit(e, false)} className="px-4 py-6">
            {activeTab === TAB_DETAILS && (
              <Stack spacing={3}>
                <div className="flex flex-col items-start gap-3">
                  <ClientDropdown
                    data={data}
                    setData={setData}
                    disabled={Boolean(recordId)}
                    hideEditButton
                  />
                  <ViewClientProfile
                    clientId={clientId}
                    clientName={data?.name}
                    variant="link"
                    displayName="View client profile"
                  />
                </div>
                <Box className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField
                    label="Occupation"
                    placeholder="Occupation"
                    name="occupation"
                    required
                    value={data?.occupation || ""}
                    onChange={handleInputChange}
                  />
                  <InputField
                    label="Course provider"
                    placeholder="Course provider"
                    name="courseProvider"
                    required
                    value={data?.courseProvider || ""}
                    onChange={handleInputChange}
                  />
                  <InputField
                    label="Course/RPL completed"
                    placeholder="Course/RP completed"
                    name="courseCompleted"
                    required
                    value={data?.courseCompleted || ""}
                    onChange={handleInputChange}
                  />

                  <Paper variant="outlined" className="border-app-border p-4 md:col-span-2">
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      Charges
                    </Typography>
                    <Box className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                      <GstChargeField
                        label="Total Government fee"
                        amountName="cost"
                        amountValue={data?.cost}
                        onAmountChange={handleInputChange}
                        gst={data?.costGst}
                        onGstChange={(next) => setData((prev) => ({ ...prev, costGst: next }))}
                      />
                      <GstChargeField
                        label="Total Service charge"
                        amountName="serviceCharge"
                        amountValue={data?.serviceCharge}
                        onAmountChange={handleInputChange}
                        gst={data?.serviceChargeGst}
                        onGstChange={(next) =>
                          setData((prev) => ({ ...prev, serviceChargeGst: next }))
                        }
                      />
                      {/* <GstChargeField
                        label="Extra charges"
                        amountName="extraCharges"
                        amountValue={data?.extraCharges}
                        onAmountChange={handleInputChange}
                        gst={data?.extraChargesGst}
                        onGstChange={(next) =>
                          setData((prev) => ({ ...prev, extraChargesGst: next }))
                        }
                      /> */}
                      {/* <GstChargeField
                        label="Kothar cost (optional)"
                        amountName="kotharCost"
                        amountValue={data?.kotharCost}
                        onAmountChange={handleInputChange}
                        gst={data?.kotharCostGst}
                        onGstChange={(next) =>
                          setData((prev) => ({ ...prev, kotharCostGst: next }))
                        }
                      /> */}
                    </Box>
                    <Box className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 pt-4 border-t border-app-border">
                      <InputField
                        label="Total (auto-calculated)"
                        placeholder="0.00"
                        name="totalPreview"
                        type="number"
                        disabled
                        value={computeTotal(
                          {
                            primaryCharge: data?.cost,
                            serviceCharge: data?.serviceCharge,
                            extraCharges: data?.extraCharges,
                          },
                          {
                            primaryChargeGst: data?.costGst,
                            serviceChargeGst: data?.serviceChargeGst,
                            extraChargesGst: data?.extraChargesGst,
                          }
                        ).toFixed(2)}
                      />
                    </Box>
                  </Paper>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Important note
                  </Typography>
                  <textarea
                    rows={4}
                    name="importantNote"
                    value={data?.importantNote || ""}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm text-app-text focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Internal notes about this skill assessment…"
                  />
                </Box>
              </Stack>
            )}

            {activeTab === TAB_DOCUMENTS && (
              <Stack spacing={2}>
                <EntryDocumentsPanel
                  moduleType="SKILL_ASSESSMENT"
                  entryId={recordId}
                  clientId={clientId}
                  documents={data.documents || []}
                  onDocumentsChange={(documents) => setData((prev) => ({ ...prev, documents }))}
                />
              </Stack>
            )}

            {activeTab === TAB_STATUS &&
              (recordId ? (
                <SkillAssessmentStatusHistoryTab skillAssessmentId={recordId} />
              ) : (
                <SaveFirstNotice />
              ))}

            {activeTab === TAB_PAYMENTS &&
              (recordId ? (
                <ModulePaymentsTab
                  moduleType="SKILL_ASSESSMENT"
                  moduleId={recordId}
                  clientId={data.clientId}
                  charges={{
                    primaryCharge: data.cost,
                    serviceCharge: data.serviceCharge,
                    extraCharges: data.extraCharges,
                    primaryChargeGst: data.costGst,
                    serviceChargeGst: data.serviceChargeGst,
                    extraChargesGst: data.extraChargesGst,
                    discount: data.discount,
                  }}
                  onPaymentSubmitted={() => setNotifyOpen(true)}
                />
              ) : (
                <SaveFirstNotice />
              ))}

            {activeTab === TAB_TASKS &&
              (recordId ? (
                <ModuleTasksTab
                  entryType="SKILL_ASSESSMENT"
                  entryId={recordId}
                  clientId={clientId}
                  clientName={data?.name}
                  taskModule="Skill Assessments"
                  assignTitle={`Skill assessment: ${data?.occupation || "Skill assessment"} (${
                    data?.name || "client"
                  })`}
                  onTasksChanged={() =>
                    queryClient.invalidateQueries({ queryKey: ["skillAssessmentDetail", routeId] })
                  }
                />
              ) : (
                <SaveFirstNotice />
              ))}

            <Divider sx={{ my: 3 }} />
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save & Exit"}
              </Button>
              <Button variant="contained" type="submit" disabled={saving}>
                {saving ? "Saving…" : recordId ? "Update assessment" : "Create assessment"}
              </Button>
            </Stack>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddSkillAssessment;
